import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const LoginForm = ({ onLogin, isLoading, error }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberDevice: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaValue, setCaptchaValue] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.username || !formData?.password) {
      return;
    }

    if (showCaptcha && !captchaValue) {
      return;
    }

    try {
      await onLogin(formData);
      // Reset failed attempts on successful login
      setFailedAttempts(0);
      setShowCaptcha(false);
    } catch (error) {
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);

      if (newFailedAttempts >= 3) {
        setShowCaptcha(true);
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const generateCaptcha = () => {
    return Math.random()?.toString(36)?.substring(2, 8)?.toUpperCase();
  };

  const [captchaCode] = useState(generateCaptcha());

  return (
    <motion.form
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      {/* Username Field */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Input
          label="👤 Usuario"
          type="text"
          placeholder="Ingrese su nombre de usuario"
          value={formData?.username}
          onChange={(e) => handleInputChange('username', e?.target?.value)}
          required
          disabled={isLoading}
          className="w-full bg-white/5 border-white/20 text-white placeholder-cyan-200/50"
        />
      </motion.div>

      {/* Password Field */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="relative"
      >
        <Input
          label="🔒 Contraseña"
          type={showPassword ? 'text' : 'password'}
          placeholder="Ingrese su contraseña"
          value={formData?.password}
          onChange={(e) => handleInputChange('password', e?.target?.value)}
          required
          disabled={isLoading}
          className="w-full pr-12 bg-white/5 border-white/20 text-white placeholder-cyan-200/50"
        />
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute right-3 top-9 text-cyan-300 hover:text-white transition-colors duration-200"
          disabled={isLoading}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
        </motion.button>
      </motion.div>

      {/* Info about auto-role detection */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-xl"
      >
        <div className="flex items-center space-x-3">
          <Icon name="Sparkles" size={20} className="text-cyan-400" />
          <div>
            <p className="text-sm font-semibold text-cyan-300">Detección Automática</p>
          </div>
        </div>
      </motion.div>

      {/* CAPTCHA (shown after failed attempts) */}
      <AnimatePresence>
        {showCaptcha && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-3 overflow-hidden"
          >
            <div className="flex items-center space-x-4 p-4 bg-white/5 border border-amber-400/30 rounded-xl">
              <div className="bg-amber-500/20 px-4 py-2 rounded font-mono text-lg tracking-wider select-none border border-amber-400/30">
                {captchaCode}
              </div>
              <motion.button
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.3 }}
                type="button"
                onClick={() => window.location?.reload()}
                className="text-amber-300 hover:text-amber-100"
              >
                <Icon name="RefreshCw" size={20} />
              </motion.button>
            </div>
            <Input
              label="🔐 Código de Verificación"
              type="text"
              placeholder="Ingrese el código mostrado"
              value={captchaValue}
              onChange={(e) => setCaptchaValue(e?.target?.value)}
              required
              disabled={isLoading}
              className="w-full bg-white/5 border-white/20 text-white placeholder-cyan-200/50"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Remember Device */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg border border-white/10"
      >
        <Checkbox
          label="📱 Recordar este dispositivo"
          checked={formData?.rememberDevice}
          onChange={(e) => handleInputChange('rememberDevice', e?.target?.checked)}
          disabled={isLoading}
        />
      </motion.div>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="flex items-center space-x-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl backdrop-blur-sm"
          >
            <Icon name="AlertCircle" size={22} className="text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Failed Attempts Warning */}
      <AnimatePresence>
        {failedAttempts > 0 && failedAttempts < 3 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="flex items-center space-x-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl backdrop-blur-sm"
          >
            <Icon name="AlertTriangle" size={22} className="text-amber-400 flex-shrink-0" />
            <span className="text-sm text-amber-300">
              ⚠️ Intento fallido {failedAttempts}/3. {3 - failedAttempts} intentos restantes.
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Login Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
      >
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading || (!formData?.username || !formData?.password)}
          iconName="LogIn"
          iconPosition="left"
          className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
        >
          {isLoading ? '🔄 Iniciando Sesión...' : '🚀 Iniciar Sesión'}
        </Button>
      </motion.div>

      {/* Forgot Password Link */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.5 }}
        className="text-center"
      >
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          type="button"
          className="text-sm text-cyan-300 hover:text-white transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto"
          disabled={isLoading}
        >
          <Icon name="HelpCircle" size={16} />
          <span>¿Olvidó su contraseña?</span>
        </motion.button>
      </motion.div>
    </motion.form>
  );
};

export default LoginForm;